<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import MoreActions from './MoreActions.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageWindow } from 'dashboard/composables/useMessageWindow';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  isSidebarOpen: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['toggleSidebar']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const currentChat = computed(() => store.getters.getSelectedChat);
const {
  hasWindow,
  state: windowState,
  remainingParts,
  WINDOW_STATE,
} = useMessageWindow(currentChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);
const isGroup = computed(() => currentChat.value?.is_group === true);
const groupName = computed(
  () => currentChat.value?.additional_attributes?.group_name || ''
);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex items-center w-full min-w-0 h-[48px] px-3 bg-white dark:bg-n-solid-3 flex-shrink-0 gap-1"
  >
    <!-- Left: back + warnings -->
    <div class="flex items-center gap-1 min-w-0 flex-1">
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="flex-shrink-0"
      />
      <div v-if="isGroup" class="flex items-center gap-1.5 min-w-0 mr-2">
        <span class="i-lucide-users size-4 text-n-teal-11 shrink-0" />
        <span class="text-sm font-semibold text-n-slate-12 truncate">
          {{ groupName }}
        </span>
      </div>
      <fluent-icon
        v-if="!isHMACVerified"
        v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
        size="14"
        class="text-n-amber-10 min-w-[14px] flex-shrink-0"
        icon="warning"
      />
      <span
        v-if="isSnoozed"
        class="text-[11px] font-medium text-n-amber-10 truncate flex-shrink-0"
      >
        {{ snoozedDisplayText }}
      </span>
      <span
        v-if="hasWindow"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium flex-shrink-0 tabular-nums"
        :class="{
          'bg-n-teal-3 text-n-teal-11': windowState === WINDOW_STATE.OPEN,
          'bg-n-amber-3 text-n-amber-11': windowState === WINDOW_STATE.CLOSING,
          'bg-n-ruby-3 text-n-ruby-11 animate-pulse':
            windowState === WINDOW_STATE.URGENT,
          'bg-n-ruby-3 text-n-ruby-11': windowState === WINDOW_STATE.CLOSED,
        }"
      >
        <span
          class="size-3"
          :class="{
            'i-lucide-clock':
              windowState === WINDOW_STATE.OPEN ||
              windowState === WINDOW_STATE.CLOSING,
            'i-lucide-alarm-clock': windowState === WINDOW_STATE.URGENT,
            'i-lucide-alarm-clock-off': windowState === WINDOW_STATE.CLOSED,
          }"
        />
        <template v-if="windowState === WINDOW_STATE.CLOSED">
          {{ $t('CONVERSATION.HEADER.WINDOW_CLOSED') }}
        </template>
        <template v-else-if="remainingParts?.kind === 'urgent'">
          {{
            $t('CONVERSATION.HEADER.WINDOW_REMAINING_URGENT', {
              time: remainingParts.time,
            })
          }}
        </template>
        <template v-else-if="remainingParts?.kind === 'hours'">
          {{
            $t('CONVERSATION.HEADER.WINDOW_REMAINING_HOURS', {
              hours: remainingParts.hours,
              minutes: remainingParts.minutes,
            })
          }}
        </template>
        <template v-else-if="remainingParts?.kind === 'minutes'">
          {{
            $t('CONVERSATION.HEADER.WINDOW_REMAINING_MINUTES', {
              minutes: remainingParts.minutes,
            })
          }}
        </template>
      </span>
    </div>

    <!-- Right: SLA + actions + sidebar toggle -->
    <div class="flex items-center gap-0.5 flex-shrink-0">
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
      <button
        class="size-8 rounded-lg hover:bg-n-alpha-2 grid place-content-center text-n-slate-11 cursor-pointer transition-colors"
        :title="isSidebarOpen ? 'Fechar painel' : 'Abrir painel'"
        @click="emit('toggleSidebar')"
      >
        <span
          :class="
            isSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          class="size-4"
        />
      </button>
    </div>
  </div>
</template>
